import type { ResourceDeployment, ResourceDeploymentStatusResp } from '@shared/deploymentApiTypes';

type DeploymentFetcher = (controller: AbortController) => Promise<ResourceDeploymentStatusResp>;
type DeploymentVisibility = {
    readonly hidden: boolean;
    addEventListener(type: 'visibilitychange', listener: () => void): void;
    removeEventListener(type: 'visibilitychange', listener: () => void): void;
};
export type DeploymentReceiptOptions = {
    accountName?: string;
    storage?: Pick<Storage, 'getItem' | 'setItem'>;
    now?: () => number;
};
export type DeploymentNotificationController = (() => void) & {
    receive: (response: ResourceDeploymentStatusResp) => void;
    refresh: () => Promise<void>;
};

const recentDeploymentMs = 5 * 60_000;
const futureToleranceMs = 60_000;

const validatedDeployment = (value: unknown): ResourceDeployment | undefined => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return;
    const deployment = value as Partial<ResourceDeployment>;
    if (typeof deployment.commit !== 'string' || !/^[0-9a-f]{40}$/.test(deployment.commit)
        || typeof deployment.syncedAt !== 'string'
        || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/.test(deployment.syncedAt)) return;
    const timestamp = Date.parse(deployment.syncedAt);
    const canonical = deployment.syncedAt.includes('.')
        ? deployment.syncedAt : deployment.syncedAt.replace('Z', '.000Z');
    if (!Number.isFinite(timestamp) || new Date(timestamp).toISOString() !== canonical) return;
    return { commit: deployment.commit, syncedAt: deployment.syncedAt };
};

/** Only complete, newer markers can advance the notification baseline. */
export class DeploymentNotificationTracker {
    private baseline?: ResourceDeployment;
    private acknowledged?: ResourceDeployment;
    private readonly receiptKey?: string;
    private readonly now: () => number;

    constructor(private readonly options: DeploymentReceiptOptions = {}) {
        this.now = options.now ?? Date.now;
        if (typeof options.accountName === 'string' && options.accountName.length > 0
            && options.accountName.length <= 128) {
            try {
                this.receiptKey = `txadmin:deployment-receipt:${encodeURIComponent(options.accountName)}`;
            } catch {
                // Unusable account identifiers fall back to this page's memory.
            }
        }
        this.acknowledged = this.readReceipt();
        this.baseline = this.acknowledged;
    }

    private readReceipt(): ResourceDeployment | undefined {
        if (!this.receiptKey || !this.options.storage) return;
        try {
            const raw = this.options.storage.getItem(this.receiptKey);
            if (!raw || raw.length > 256) return;
            const receipt = JSON.parse(raw);
            if (!receipt || typeof receipt !== 'object' || Array.isArray(receipt)
                || receipt.schema !== 1 || Object.keys(receipt).length !== 3) return;
            const deployment = validatedDeployment(receipt);
            return deployment && Date.parse(deployment.syncedAt) <= this.now() + futureToleranceMs
                ? deployment : undefined;
        } catch {
            // Browser storage may be disabled or contain unrelated invalid data.
        }
    }

    /** Persist only markers already displayed or deliberately baselined as old. */
    acknowledge(deployment: ResourceDeployment) {
        const valid = validatedDeployment(deployment);
        if (!valid || Date.parse(valid.syncedAt) > this.now() + futureToleranceMs) return;
        const timestamp = Date.parse(valid.syncedAt);
        if ([this.acknowledged, this.readReceipt()]
            .some((receipt) => receipt && Date.parse(receipt.syncedAt) > timestamp)) return;
        this.acknowledged = valid;
        if (!this.receiptKey || !this.options.storage) return;
        try {
            this.options.storage.setItem(this.receiptKey, JSON.stringify({ schema: 1, ...valid }));
        } catch {
            // In-memory duplicate protection still works when persistence fails.
        }
    }

    isAcknowledged(deployment: ResourceDeployment) {
        const timestamp = Date.parse(deployment.syncedAt);
        return [this.acknowledged, this.readReceipt()].some((receipt) => receipt
            && (receipt.commit === deployment.commit || Date.parse(receipt.syncedAt) > timestamp));
    }

    accept(response: ResourceDeploymentStatusResp): ResourceDeployment | undefined {
        if (!response || typeof response !== 'object' || !('deployment' in response)) return;
        const deployment = validatedDeployment(response.deployment);
        if (!deployment) return;
        const timestamp = Date.parse(deployment.syncedAt);
        const age = this.now() - timestamp;
        if (age < -futureToleranceMs) return;

        if (!this.baseline) {
            this.baseline = deployment;
            if (age <= recentDeploymentMs && !this.isAcknowledged(deployment)) return deployment;
            this.acknowledge(deployment);
            return;
        }
        if (timestamp < Date.parse(this.baseline.syncedAt)) return;
        const changed = deployment.commit !== this.baseline.commit;
        this.baseline = deployment;
        return changed && !this.isAcknowledged(deployment) ? deployment : undefined;
    }
}

/** Consume the privileged stream, reconciling its marker at startup or reconnect. */
export function startDeploymentNotifications(
    fetchStatus: DeploymentFetcher,
    notify: (deployment: ResourceDeployment) => void,
    visibility: DeploymentVisibility | undefined = typeof document === 'undefined' ? undefined : document,
    receiptOptions: DeploymentReceiptOptions = {},
): DeploymentNotificationController {
    const tracker = new DeploymentNotificationTracker(receiptOptions);
    let active = true;
    let inFlight: AbortController | undefined;
    let pending: ResourceDeployment | undefined;

    const announce = (deployment: ResourceDeployment) => {
        if (visibility?.hidden) {
            pending = deployment;
        } else {
            pending = undefined;
            if (!tracker.isAcknowledged(deployment)) {
                notify(deployment);
                tracker.acknowledge(deployment);
            }
        }
    };
    const onVisibilityChange = () => {
        if (!active || visibility?.hidden || !pending) return;
        const deployment = pending;
        pending = undefined;
        if (!tracker.isAcknowledged(deployment)) {
            notify(deployment);
            tracker.acknowledge(deployment);
        }
    };

    const receive = (response: ResourceDeploymentStatusResp) => {
        if (!active) return;
        const deployment = tracker.accept(response);
        if (deployment) announce(deployment);
    };

    const refresh = async () => {
        if (!active || inFlight) return;
        const controller = new AbortController();
        inFlight = controller;
        let timeout: ReturnType<typeof setTimeout> | undefined;
        let abortHandler: (() => void) | undefined;
        try {
            const response = await Promise.race([
                fetchStatus(controller),
                new Promise<never>((_, reject) => {
                    abortHandler = () => reject(new Error('Deployment check cancelled'));
                    controller.signal.addEventListener('abort', abortHandler, { once: true });
                    timeout = setTimeout(() => controller.abort(), 7_500);
                }),
            ]);
            if (!active || controller.signal.aborted) return;
            receive(response);
        } catch {
            // Failed checks leave the last valid marker intact.
        } finally {
            clearTimeout(timeout);
            if (abortHandler) controller.signal.removeEventListener('abort', abortHandler);
            if (inFlight === controller) inFlight = undefined;
        }
    };

    visibility?.addEventListener('visibilitychange', onVisibilityChange);
    void refresh();
    const stop = () => {
        active = false;
        pending = undefined;
        visibility?.removeEventListener('visibilitychange', onVisibilityChange);
        inFlight?.abort();
    };
    return Object.assign(stop, { receive, refresh });
}
